/**
 * Typed accessors for the `/devices` admin Backend_API routes
 * (Requirement 12). One hook returning small `request` wrappers,
 * memoized on the underlying API client identity so consumers can
 * use them as `useEffect` / `useCallback` deps.
 *
 * Wire shape mirrors `packages/api/src/handlers/devices.ts` — the API
 * server is the single source of truth for these payloads.
 */

import { useMemo } from 'react';
import type { DeviceRegistryEntry } from '@tcvs/shared';
import { useApi } from '../api';
import type { ApiResult } from '../auth/api-client';

/** Response shape of `GET /devices`. */
export interface ListDevicesResponse {
  items: DeviceRegistryEntry[];
}

/** Optional metadata sent on `POST /devices`. */
export interface RegisterDeviceInput {
  name?: string;
  location?: string;
}

/**
 * Wire shape of the `POST /devices` response. The Lambda returns the
 * one-shot Device_Credential bundle here and never again (Requirement
 * 12.2). Kept local to the SPA — this is a wire shape, not a domain
 * type, and we want the boundary explicit.
 */
export interface RegisterDeviceResponse {
  deviceId: string;
  iotThingName: string;
  certificateId: string;
  certificateArn: string;
  /** PEM-encoded X.509 client certificate. Returned exactly once. */
  certificate: string;
  /** PEM-encoded RSA private key. Returned exactly once; never persisted. */
  privateKey: string;
  /** AWS-published ATS root CA URL the Pi should trust. */
  rootCaUrl: string;
  /** Fully-qualified mTLS endpoint hostname. */
  iotEndpoint: string;
  /** ISO 8601 timestamp the Lambda assigned at registration. */
  issuedAt: string;
}

/** Wire shape of the `DELETE /devices/{id}` response. */
export interface RevokeDeviceResponse {
  deviceId: string;
  status: 'REVOKED';
  revokedAt: string;
}

/** React hook returning typed accessors for the device admin routes. */
export function useDevicesApi() {
  const api = useApi();
  return useMemo(
    () => ({
      list: (): Promise<ApiResult<ListDevicesResponse>> =>
        api.request<ListDevicesResponse>('/devices', { method: 'GET' }),
      register: (input: RegisterDeviceInput): Promise<ApiResult<RegisterDeviceResponse>> =>
        api.request<RegisterDeviceResponse>('/devices', {
          method: 'POST',
          body: input,
        }),
      revoke: (deviceId: string): Promise<ApiResult<RevokeDeviceResponse>> =>
        api.request<RevokeDeviceResponse>(`/devices/${encodeURIComponent(deviceId)}`, {
          method: 'DELETE',
        }),
    }),
    [api],
  );
}

/**
 * Build the dotenv snippet shipped inside the credential bundle
 * download. The snippet uses the conventional Pi paths from the
 * deploy-pi.sh runbook (`/home/pi/firmware/certs/...`) so the operator
 * can scp the cert files alongside the .env file with no edits.
 *
 * Exported so unit tests can lock the snippet shape down without
 * spinning up the page.
 */
export function buildFirmwareEnv(response: RegisterDeviceResponse): string {
  return [
    '# Generated by the TCVS Devices admin page on registration.',
    '# Save the cert/key/root-ca files alongside this .env on the Pi at',
    '#   /home/pi/firmware/certs/{device.pem.crt,device.pem.key,AmazonRootCA1.pem}',
    '# and place this file at /home/pi/firmware/.env (chmod 600).',
    '',
    `TCVS_DEVICE_ID=${response.deviceId}`,
    `TCVS_IOT_ENDPOINT=${response.iotEndpoint}`,
    'TCVS_CERT_PATH=/home/pi/firmware/certs/device.pem.crt',
    'TCVS_KEY_PATH=/home/pi/firmware/certs/device.pem.key',
    'TCVS_ROOT_CA_PATH=/home/pi/firmware/certs/AmazonRootCA1.pem',
    '',
  ].join('\n');
}
